import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";
import { components, spacing, effects, responsive, typography, colors } from "../../../design/tokens";

interface BlogPost {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  coverImage: string;
  dateFolder: string;
}

export function WelcomeBlogFeed() {
  const isCompactView = useIsCompactView();
  const [blogPosts, setBlogPosts] = useState<BlogPost[] | null>(null);

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const response = await fetch("/blog/index.json");
        if (!response.ok) {
          throw new Error("Failed to load blog posts");
        }
        const posts = await response.json();
        setBlogPosts(posts);
      } catch (error) {
        console.error("Error loading blog posts:", error);
        setBlogPosts([]);
      }
    };

    fetchBlogPosts();
  }, []);

  const getContent = () => {
    return isCompactView ? <CompactFeed posts={blogPosts} /> : <DefaultFeed posts={blogPosts} />;
  };

  if (blogPosts === null) {
    return <div className="h-8"></div>;
  }

  return <div className="w-full py-8 pb-16">{getContent()}</div>;
}

function CompactFeed({ posts }: { posts: BlogPost[] | null }) {
  var settings = {
    dots: true,
    customPaging: () => (
      <div className="h-3 w-3 rounded-full transition-all duration-300 bg-[#989CA933] dark:bg-[#181A1D]" />
    ),
    dotsClass: "slick-dots flex items-center justify-center space-x-2 pt-12",
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 6000,
    pauseOnHover: true,
    pauseOnFocus: true,
    draggable: false,
    swipe: true,
    touchMove: true,
  };

  return (
    <Slider {...settings}>
      {posts!.map((post) => (
        <BlogPostCard key={post.slug} post={post} />
      ))}
    </Slider>
  );
}

function DefaultFeed({ posts }: { posts: BlogPost[] | null }) {
  var settings = {
    dots: true,
    customPaging: () => (
      <div className="h-3 w-3 rounded-full transition-all duration-300 bg-[#989CA933] dark:bg-[#181A1D]" />
    ),
    dotsClass: "slick-dots flex items-center justify-center space-x-2 pt-12",
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 6000,
    pauseOnHover: true,
    pauseOnFocus: true,
    draggable: false,
    swipe: false,
    touchMove: false,
  };

  return (
    <Slider {...settings}>
      {posts!.map((post) => (
        <div key={post.slug} className="px-2.5">
          <BlogPostCard post={post} />
        </div>
      ))}
    </Slider>
  );
}


function BlogPostCard({ post }: { post: BlogPost }) {
  const isCompactView = useIsCompactView();
  const navigate = useNavigate();

  return (
    <div
      id={`home-blog-post-card-${post.slug}`}
      className={`${components.card.blog} ${responsive("mx-2", "", isCompactView)}`}
      onClick={() => {
        navigate(`/blog/${post.slug}`);
      }}
      style={{
        boxShadow: effects.shadow.custom.card,
      }}
    >
      {/* Gradient glow on hover */}
      <div
        className={`absolute inset-0 ${effects.borderRadius.xl} ${effects.transition.opacity} opacity-0 group-hover:opacity-100 pointer-events-none`}
        style={{
          boxShadow: effects.shadow.custom.glow,
        }}
      />

      <div className={`relative overflow-hidden ${spacing.height.blogImage}`}>
        {/* Gradient overlay on hover */}
        <div
          className={`absolute inset-0 ${effects.transition.opacity} opacity-0 group-hover:opacity-100 z-10`}
          style={{
            background: 'linear-gradient(135deg, rgba(233, 30, 99, 0.15), rgba(156, 39, 176, 0.2))',
          }}
        />
        {post.coverImage ? (
          <img
            src={post.coverImage}
            alt={post.title}
            className={`w-full h-full object-cover ${effects.transition.transform} group-hover:scale-105`}
            style={{
              imageRendering: 'crisp-edges',
              willChange: 'transform',
              backfaceVisibility: 'hidden',
            }}
            loading="lazy"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = "none";
            }}
          />
        ) : null}
      </div>

      <div
        className={`pt-3 pb-3 px-4 ${spacing.height.blogContent}
      flex flex-col justify-center relative z-10`}
      >
        <h3
          className={`${typography.weight.bold} ${colors.text.primary} ${typography.size.sm} ${typography.leading['1.4']} overflow-hidden`}
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            textOverflow: "ellipsis",
            wordBreak: "break-word",
            maxHeight: "2.8em",
          }}
        >
          {post.title}
        </h3>
      </div>
    </div>
  );
}

